import { inject, Injectable } from '@angular/core';
import { filter, map, Observable, share } from 'rxjs';
import { SseClient } from './sse-client.service';
import { ConfigService } from '../config/config.service';
import { BackendUrlConfig } from '../config/backend-url-config';

/**
 * Named events on the auftrag stream. Consumers switch on these and ignore everything else, so the
 * backend can add event types without breaking anyone.
 */
export const AuftragEventName = {
  CHANGED: 'auftrag-changed',
} as const;

/**
 * Fired whenever an Auftrag was created, changed status, or changed hands — including when the
 * assignment was *removed*, because a takeover reaches ps-server as revoke-then-claim.
 */
export interface AuftragChangedEvent {
  auftragId: string;
}

/** The `/api/v2` counterpart to {@link EingangEventsService} */
@Injectable({
  providedIn: 'root',
})
export class AuftragEventsService {
  private readonly sseClient = inject(SseClient);
  private readonly configService = inject(ConfigService<BackendUrlConfig>);

  readonly auftragChanged$: Observable<AuftragChangedEvent> = this.sseClient
    .connect(`${this.configService.config.psServerUrl}/api/v2/auftraege/live`)
    .pipe(
      filter((frame) => frame.event === AuftragEventName.CHANGED),
      map((frame) => parseAuftragChanged(frame.data)),
      filter((event): event is AuftragChangedEvent => event !== null),
      share(),
    );
}

function parseAuftragChanged(data: string): AuftragChangedEvent | null {
  try {
    const parsed = JSON.parse(data) as Partial<AuftragChangedEvent>;
    return parsed.auftragId ? { auftragId: parsed.auftragId } : null;
  } catch {
    return null;
  }
}
